/**
 * HOOK: useTokens
 * Reads from the global token store — single fetch, shared across all consumers.
 * Triggers a fetch on first call for the current user.
 */

import { useEffect } from 'react';
import { useAuth } from '@/components/auth/AuthProvider';
import { supabase } from '@/lib/supabase';
import { useTokenStore } from '@/stores/token-store';

export function useTokens() {
  const { user } = useAuth();
  const store = useTokenStore();

  useEffect(() => {
    if (!user) return;

    // Fetch balance (deduplicated by store: won't double-fetch within 10s)
    store.fetchBalance(user.id);

    // Realtime: refresh on profile change (e.g. after token deduction)
    const channel = supabase
      .channel(`token-changes-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        () => {
          store.fetchBalance(user.id, true); // force=true to bypass cache
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]); // Only re-run if userId changes

  return {
    balance: store.balance,
    breakdown: store.breakdown,
    subscriptionTier: store.subscriptionTier,
    monthlyAllowance: store.monthlyAllowance,
    monthlyUsed: store.monthlyUsed,
    resetAt: store.resetAt,
    totalPurchased: store.totalPurchased,
    totalUsed: store.totalUsed,
    loading: store.loading,
    hasEnoughTokens: store.hasEnoughTokens,
    refresh: () => user && store.fetchBalance(user.id, true),
  };
}
